/**
 * @module components/StoryPostcard
 * @description The "Postcard from Jamaica" card front, rebuilt as live HTML.
 *
 * It is a fixed 3:2 object whose inner sizes use container-query units, so
 * it renders identically as a gallery thumbnail, a full-screen view, a PNG
 * export, a 4 × 6 or 6 × 9 in print and a print-shop PDF. Layout follows
 * Samantha's reference cards (see design/cardLayout.ts).
 *
 * In the adaptive layout (app/cardOrientation.tsx) the same card turns
 * portrait on phones: photo above the story, everything larger. The
 * `postcard--portrait` class carries the portrait type scale.
 */
import { forwardRef, type CSSProperties, type ReactNode } from "react";
import { Split } from "@jochantrelle/golden-ratio-splitter";
import type { StoryCard } from "../domain/types";
import type { EditionConfig } from "../config/editions";
import { CAMPAIGN } from "../config/campaign";
import { STORY_PHOTO_SHARE } from "../design/cardLayout";
import { useEdition } from "../app/edition";
import { useCardOrientation, type CardOrientation } from "../app/cardOrientation";
import { assetUrl } from "../lib/assetUrl";
import { BrandLockup, Postmark } from "./Brand";

export interface StoryPostcardProps {
    card: StoryCard;
    /** Optional extra class (e.g. gallery hover treatment). */
    className?: string;
    /** Renders for a specific edition instead of the active one (print studio, previews). */
    edition?: EditionConfig;
}

/** Sets the character count CSS uses to shrink long lines to fit their column. */
export const fitChars = (text: string): CSSProperties => ({ "--chars": Math.max(text.length, 1) }) as CSSProperties;

/** Shared header for every card front: lockup, kicker, title, postmark. */
export function CardHeader({ kicker, title, postmark }: { kicker: string; title: string; postmark: EditionConfig["postmark"] }) {
    return (
        <header className="pc-head">
            <BrandLockup variant="horizontal" className="pc-lockup" />
            <span className="pc-vrule" aria-hidden="true" />
            <div className="pc-titles">
                <p className="pc-kicker">{kicker}</p>
                <h2 className="pc-title" style={fitChars(title)}>{title}</h2>
            </div>
            <Postmark kind={postmark} />
        </header>
    );
}

/** Class for the card's current orientation; every face uses it. */
export const orientationClass = (orientation: CardOrientation): string => (orientation === "portrait" ? "postcard--portrait" : "");

/**
 * Photo + story body. Landscape: side by side at `share` through the shared
 * Split primitive. Portrait: stacked, each at full width.
 */
export function CardBody({ orientation, share, className = "", photo, story }: { orientation: CardOrientation; share: number; className?: string; photo: ReactNode; story: ReactNode }) {
    return orientation === "portrait" ? (
        <div className={`pc-body pc-body--stack ${className}`}>
            {photo}
            {story}
        </div>
    ) : (
        <div className={`pc-body ${className}`}>
            <Split axis="column" ratio={share}>
                {photo}
                {story}
            </Split>
        </div>
    );
}

/** Visible marker for records waiting on client approval. */
export function ContentHoldNotice() {
    return <p className="pc-hold">Content hold · not for print</p>;
}

export const StoryPostcard = forwardRef<HTMLElement, StoryPostcardProps>(function StoryPostcard({ card, className, edition }, ref) {
    const active = useEdition();
    const ed = edition ?? active;
    const orientation = useCardOrientation();
    const statLines = card.statValue.split("\n");
    return (
        <article ref={ref} className={`postcard ${orientationClass(orientation)} ${className ?? ""}`} aria-label={`Postcard: ${card.title}`}>
            <CardHeader kicker="Postcard from Jamaica" title={card.title} postmark={ed.postmark} />

            <CardBody
                orientation={orientation}
                share={STORY_PHOTO_SHARE}
                photo={
                    <figure className="pc-photo">
                        <img src={assetUrl(card.image.src)} alt={card.image.alt} />
                        {card.image.isIllustrative && <figcaption className="pc-badge">Illustrative image</figcaption>}
                    </figure>
                }
                story={
                    <div className="pc-story">
                        <p className={`pc-stat ${statLines.length > 1 ? "pc-stat--multi" : ""}`} aria-label={statLines.join(" ")}>
                            {statLines.map((line, i) => (
                                <span key={i} className="pc-stat-line" style={fitChars(line)}>
                                    {line}
                                </span>
                            ))}
                        </p>
                        <p className="pc-stat-label">{card.statLabel}</p>
                        <p className="pc-copy">{card.body}</p>
                        {card.contentHold ? (
                            <ContentHoldNotice />
                        ) : (
                            <>
                                <hr className="pc-rule" />
                                <p className="pc-tagline">
                                    Stories like this happen because friends like you trust AFJ to give where the need is greatest.
                                </p>
                            </>
                        )}
                    </div>
                }
            />

            <footer className="pc-foot">
                <div>
                    <p className="pc-love">This is your love at work.</p>
                    <p className="pc-sub">{ed.card.footerSub}</p>
                </div>
                <p className="pc-script">{CAMPAIGN.campaignName}.</p>
            </footer>
        </article>
    );
});
